/* eslint-disable max-len */
/* istanbul ignore file */
/**
 * Module dependencies
 */
const React = require('react');
const { string, shape, bool, func } = require('prop-types');
const injectI18n = require('nordic/i18n/injectI18n');

const { connect } = require('react-redux');

const { Card, CardContent } = require('@andes/card');
const { List, ListItem } = require('@andes/list');
const { Title } = require('@andes/typography');
const { Pill } = require('@andes/badge');
const { useUrlState } = require('../../utils/context/url');
const { usePlatformState } = require('../../utils/context/platform');
const { useUserState } = require('../../utils/context/user');
const { useChatState } = require('../../utils/context/chatProvider');
const { getActiveHelpChat } = require('../../browserServices/checkActiveHelpChat');

const { removeChatStatus, setChatStatus } = require('../../browserServices/chatStatus');
const useContactRedirect = require('../../hooks/useContactRedirect');

const { useRef, useEffect, useState } = React;

const ContactButtonV2 = (props) => {
  const {
    omniOneClickButton,
    contactData,
    i18n,
    isNativeMobile,
    title,
    performContact,
    customSourceId,
    desktopChatEnabled,
    flow,
  } = props;

  const [link, setLink] = useState(undefined);
  const [chatId, setChatId] = useState();
  const { homeUrl, baseUrl } = useUrlState();
  const { chatReference } = useChatState();
  const { siteId } = usePlatformState();
  const user = useUserState();
  const userSiteId = user && user.raw ? user.raw.site_id : siteId;
  const contactBtnRef = useRef(null);
  const oocTitle = i18n.gettext('¿No encontraste lo que buscabas?');
  const ocTitle = i18n.gettext('¿Necesitas más ayuda?');
  const chatUrl = `${homeUrl}${baseUrl}/chat?flow=${flow}`;

  const sendMelidataMelichatTrack = () => {
    if (chatId) {
      const eventData = {
        chat_id: String(chatId),
        origin: 'oneclick',
        has_messages: false,
        tags: [],
        portal_source_id: customSourceId,
      };
      if (window && window.melidata) {
        window.melidata('cleanAndSend', 'event', {
          path: '/portal/melichat/return',
          event_data: eventData,
        });
      }
    }
  };
  const sendMelidataExperimentTrack = () => {
    if (window && window.melidata) {
      window.melidata('cleanAndSend', 'event', {
        path: '/contact_button',
        event_data: {},
      });
    }
  };
  const { handleClick } = useContactRedirect({...props, link, sendMelidataMelichatTrack, sendMelidataExperimentTrack });

  const hasActiveChat = (helpChatData) => (helpChatData?.data[0]?.case_id && helpChatData?.data[0]?.provider === 'MELICHAT');

  const getHelpChatStatus = async () => {
    const response = await getActiveHelpChat();

    if (hasActiveChat(response)) {
      setChatStatus('role', 'CX');
      hasChatActiveHelpChat(response);
    } else {
      setLink(undefined);
      removeChatStatus('role');
    }
  };

  const hasChatActiveHelpChat = (response) => {
    const data = response.data[0];
    const caseId = data.case_id;
    const chatId = data.chat_id;
    const [base] = chatUrl.split('?');
    const redirectUrl = `${base}/v2?chat_id=PLACEHOLDER_CHAT_ID&case_id=PLACEHOLDER_CASE_ID`;
    const chatLink = redirectUrl
      .replace('PLACEHOLDER_CHAT_ID', chatId)
      .replace('PLACEHOLDER_CASE_ID', caseId);
    setChatId(chatId);
    setLink(chatLink);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      getHelpChatStatus();

      window.addEventListener('focus', () => {
        getHelpChatStatus();
      });
    }
  }, []);

  useEffect(() => {
    if (performContact && contactBtnRef && contactBtnRef.current) {
      contactBtnRef.current.click();
    }
  });

  const backToChatText = i18n.gettext('Volver al chat en curso');
  const ongoingAssistanceText = i18n.gettext('Asistencia en curso');

  const renderPillText = () => {
    if (chatId && desktopChatEnabled) {
      return <Pill color="accent" size="small">{backToChatText}</Pill>;
    }
    if (chatReference) {
      return <Pill color="accent" size="small">{ongoingAssistanceText}</Pill>;
    }

    return null;
  };

  const contactText = i18n.gettext('Contáctanos');

  return ((isNativeMobile && contactData && contactData.context && contactData.context.user_id === -1) || (performContact && siteId !== userSiteId))
    ? false
    : (
      <div className={`cx-contact-button${omniOneClickButton ? '__ooc' : ''}`}>
        {!omniOneClickButton
          && (
            <>
              <div className="cx-contact-button__title">
                <Title component="h2" size="s" color="primary" className="section-title">{title || ocTitle}</Title>
              </div>
              <Card paddingSize={0}>
                <List srLabel={contactText}>
                  <ListItem
                    onClick={(e) => handleClick(e)}
                    className="cx-contact-button__item"
                    title={contactText}
                    rightContent={renderPillText()}
                    data-testid="contact-button"
                    ref={contactBtnRef}
                    chevron
                  />
                </List>
              </Card>
            </>
          )}
        {omniOneClickButton && (
          <Card paddingSize={16} role="button" aria-label={contactText}>
            <CardContent className="cx-contact-button__ooc--card">
              <span className="cx-contact-button__ooc--title">{oocTitle}</span>
              <a role="button" tabIndex="0" onClick={(e) => handleClick(e)} className="cx-contact-button__ooc--link">
                {contactText}
              </a>
            </CardContent>
          </Card>
        )}
      </div>
    );
};

/**
 * Define props
 */
ContactButtonV2.propTypes = {
  i18n: shape({
    gettext: func.isRequired,
  }).isRequired,
  contactData: shape({
    deepLink: string,
    context: shape({
      title: string,
    }),
  }).isRequired,
  isNativeMobile: bool,
  chatUrlBase: string,
  title: string,
  asRow: bool,
  itemTitle: string,
  performContact: bool,
  isSocialNetwork: bool,
  omniOneClickButton: bool,
  desktopChatEnabled: bool,
  customSourceId: string,
  contentId: string,
  chat: string,
  flow: string,
};

ContactButtonV2.defaultProps = {
  isNativeMobile: false,
  title: undefined,
  asRow: false,
  itemTitle: undefined,
  performContact: false,
  isSocialNetwork: false,
  omniOneClickButton: false,
  desktopChatEnabled: false,
  customSourceId: undefined,
  contentId: undefined,
  chat: undefined,
  chatUrlBase: undefined,
  flow: undefined,
};

const mapStateToProps = (
  {
    profiles: {
      contactData,
      performContact,
      isSocialNetwork,
      customSourceId,
      contentId,
      desktopChatEnabled,
      userSegment,
    },
    base: { isNativeMobile },
  },
) => ({
  contactData,
  performContact,
  isSocialNetwork,
  customSourceId,
  isNativeMobile,
  contentId,
  desktopChatEnabled,
  userSegment,
});


module.exports = injectI18n(connect(mapStateToProps, {})(ContactButtonV2));
